// ./src/components/features/MarketingChannels/MarketingChannelsList/MarketingChannelsList.tsx
//
// Marketing channels list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit/Delete only), rights-gated per row, selection-ready.
// This is the account-level CRUD list (Library). The per-event "get URLs / QR"
// view is a separate component. Mirrors the Vouchers list conventions
// (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Code,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { MarketingChannel } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalMarketingChannelsContext } from "@/contexts/MarketingChannelsContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Props
// ============================================

export interface MarketingChannelsListProps {
  data?: MarketingChannel[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditChannel?: (channel: MarketingChannel) => void;
  onDeleteChannel?: (channel: MarketingChannel) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddChannel?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no
   * match" — NOT "nothing here yet", so don't offer the first-run Add prompt.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

// The ?ch= marker, shown as a compact code chip.
const MarkerChip: React.FC<{ channel: MarketingChannel }> = ({ channel }) => {
  if (!channel.code) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return <Code>{`?ch=${channel.code}`}</Code>;
};

const ChannelIcon: React.FC<{ size?: number }> = ({ size = 34 }) => (
  <ThemeIcon variant="light" color="teal" radius="xl" size={size}>
    <AppIcon iconName="Speakerphone" size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  channel: MarketingChannel;
  onEdit?: (c: MarketingChannel) => void;
  onDelete?: (c: MarketingChannel) => void;
  isDeleting: boolean;
}> = ({ channel, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(channel, "edit");
  const showDelete = !!onDelete && recordHasRight(channel, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(channel)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(channel)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  channel: MarketingChannel;
  name: string;
  onEdit?: (c: MarketingChannel) => void;
  onDelete?: (c: MarketingChannel) => void;
  highlighted: boolean;
  isDeleting: boolean;
  language: string;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const ChannelTableRow: React.FC<RowProps> = ({
  channel,
  name,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => channel.id && onToggleSelect?.(channel.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap" align="center">
          <ChannelIcon />
          <Box style={{ minWidth: 0 }}>
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            {channel.description && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {channel.description}
              </Text>
            )}
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 220 }}>
        <MarkerChip channel={channel} />
      </Table.Td>
      <Table.Td style={{ width: 160 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(channel.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions channel={channel} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const ChannelTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 220 }}>
      <Skeleton height={18} width={120} radius="sm" />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Skeleton height={14} width={110} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const ChannelCardComponent: React.FC<CardProps> = ({
  channel,
  name,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => channel.id && onToggleSelect?.(channel.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(channel, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(channel),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(channel, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(channel),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, channel, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <ChannelIcon size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="md" fw={600} lineClamp={1}>
            {name}
          </Text>
          <Box mt={6}>
            <MarkerChip channel={channel} />
          </Box>
          {channel.description && (
            <Text size="sm" c="dimmed" lineClamp={2} mt={6}>
              {channel.description}
            </Text>
          )}
        </Box>
        {showActions && (
          <RowActions channel={channel} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
        )}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const ChannelCard = React.memo(ChannelCardComponent);

const ChannelCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={2} gap={6} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const MarketingChannelsListComponent: React.FC<MarketingChannelsListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditChannel,
  onDeleteChannel,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddChannel,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["marketingChannels", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditChannel, onDeleteChannel);

  const channelsContext = useOptionalMarketingChannelsContext();

  const channels = dataProp ?? channelsContext?.marketingChannels ?? [];
  const loading = loadingProp ?? channelsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? channelsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? channelsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? channelsContext?.loadMore;
  const isRemoving = isRemovingProp ?? channelsContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: channels.length });

  const handleEdit = useCallback(
    (channel: MarketingChannel) => {
      closeAllCards();
      onEditChannel?.(channel);
    },
    [onEditChannel, closeAllCards]
  );

  const handleDelete = useCallback(
    (channel: MarketingChannel) => {
      closeAllCards();
      onDeleteChannel?.(channel);
    },
    [onDeleteChannel, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = channelsContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && channels.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("marketingChannels:emptyState.noMatch", {
            defaultValue: "No channels match your search or filters.",
          })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("marketingChannels:emptyState.title", { defaultValue: "No marketing channels yet" })}
          description={t("marketingChannels:emptyState.description", {
            defaultValue:
              "Add a channel (e.g. Facebook → fb) to tag your event links and see which sources convert best.",
          })}
          iconName="Speakerphone"
          actionLabel={t("marketingChannels:emptyState.actionLabel", { defaultValue: "Add channel" })}
          onAction={onAddChannel}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (channel: MarketingChannel): boolean =>
    isSelectable &&
    ((!!onEditChannel && recordHasRight(channel, "edit")) ||
      (!!onDeleteChannel && recordHasRight(channel, "remove")));

  const rowProps = (channel: MarketingChannel): RowProps => ({
    channel,
    name: channel.name || "—",
    onEdit: onEditChannel ? handleEdit : undefined,
    onDelete: onDeleteChannel ? handleDelete : undefined,
    highlighted: channel.id === highlightedId,
    isDeleting: isRemoving(channel.id),
    language,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(channel),
    selected: selection?.isSelected(channel.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = channels
    .filter((c) => rowIsSelectable(c))
    .map((c) => c.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("marketingChannels:list.columns.name", { defaultValue: "Channel" })}</Table.Th>
                <Table.Th style={{ width: 220 }}>
                  {t("marketingChannels:list.columns.marker", { defaultValue: "Marker" })}
                </Table.Th>
                <Table.Th style={{ width: 160 }}>
                  {t("marketingChannels:list.columns.createdAt", { defaultValue: "Added" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <ChannelTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : channels.map((channel) => <ChannelTableRow key={channel.id} {...rowProps(channel)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <ChannelCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {channels.map((channel) => (
            <motion.div
              key={channel.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <ChannelCard
                {...rowProps(channel)}
                forceClose={shouldForceClose(channel.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(channel.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(channel.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const MarketingChannelsList = React.memo(MarketingChannelsListComponent);
